import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { isTabletOrMobileDevice } from 'constants/app';
import {
  getIsMarketGraphsDarkThemeEnabled,
  getIsMarketGraphsSimplifiedViewEnabled,
  getMarketGraphIsSmallScreenView
} from 'redux/modules/appConfigs/selectors';
import {
  getIsMarketDefinitionMarketClosed,
  getIsMarketDefinitionSelectionClosed
} from 'redux/modules/marketDefinitions/selectors';

import MarketGraphChart from '../MarketGraphChart/MarketGraphChart';
import MarketGraphLiquidityTradedTable from '../MarketGraphLiquidityTradedTable/MarketGraphLiquidityTradedTable';
import MarketGraphTotalMatchedBlock from '../MarketGraphTotalMatchedBlock/MarketGraphTotalMatchedBlock';

import styles from './MarketGraphContent.module.scss';

export default function MarketGraphContent() {
  const { t } = useTranslation();
  const {
    marketId = '',
    selectionId = '',
    handicap = 0
  } = useParams<{ marketId: string; selectionId: string; handicap: string }>();

  const isSmallScreenView = useSelector(getMarketGraphIsSmallScreenView);
  const isMarketClosed = useSelector(getIsMarketDefinitionMarketClosed(marketId));
  const isSimplifiedView = useSelector(getIsMarketGraphsSimplifiedViewEnabled);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const isSelectionClosed = useSelector(
    getIsMarketDefinitionSelectionClosed({ marketId, selectionId: Number(selectionId), handicap: Number(handicap) })
  );

  const isMobile = isSmallScreenView || isTabletOrMobileDevice;

  if (isMarketClosed || isSelectionClosed) {
    return (
      <div
        className={classNames(styles.content__wrapper, styles.content__wrapper__closedMarketSelection, {
          [styles.content__wrapper__mobile]: isTabletOrMobileDevice,
          [styles.content__wrapper__dark]: isDarkTheme
        })}
      >
        <div className={styles.content__closed}>
          <i className={classNames('biab_custom-icon-selection-closed', styles.content__icon)} />
          <p className={styles.content__closed__text}>
            {t(isMarketClosed ? 'marketGraph.labels.marketClosed' : 'marketGraph.labels.selectionClosed')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.content__wrapper, {
        [styles.content__wrapper__mobile]: isTabletOrMobileDevice,
        [styles.content__wrapper__dark]: isDarkTheme
      })}
    >
      <div
        className={classNames(styles.content__container, {
          [styles.content__container__smallScreen]: isSmallScreenView,
          [styles.content__container__mobile]: isTabletOrMobileDevice
        })}
      >
        <div
          className={classNames(styles.content__left, {
            [styles.content__left__smallScreen]: isSmallScreenView,
            [styles.content__left__mobile]: isTabletOrMobileDevice,
            [styles.content__left__simplified]: isSimplifiedView
          })}
        >
          <MarketGraphTotalMatchedBlock />
          <MarketGraphChart />
        </div>
        {!isSimplifiedView && <MarketGraphLiquidityTradedTable />}
      </div>
      <p
        className={classNames(styles.content__footer, {
          [styles.content__footer__mobile]: isMobile
        })}
      >
        {t('marketGraph.labels.footer')}
      </p>
    </div>
  );
}
